import { FileText } from "lucide-react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Tooltip, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface Props {
  count: number;
  className?: string;
  /**
   * Visual style:
   * - dark: navy bg + gold icon (hero thumbnails over dark bg)
   * - light: light surface + dark text (catalog cards on light bg)
   * - blue: white bg + blue icon (catalog cards, requested treatment)
   */
  variant?: "dark" | "light" | "blue";
  /** Visual size — md for hero, lg for catalog cards / large surfaces. */
  size?: "sm" | "md" | "lg";
}

/**
 * Small bottom-right badge indicating that a video has PDF/image resources attached.
 * Uses Radix Tooltip Portal so the label is not clipped by overflow-hidden parents
 * (e.g. hero pager cards).
 */
export const VideoPdfIndicator = ({
  count,
  className = "",
  variant = "dark",
  size = "md",
}: Props) => {
  const { t } = useTranslation("common");
  if (!count) return null;

  const palette =
    variant === "dark"
      ? "bg-[#070e1e]/85 text-[#fcb424] border border-[#fcb424]/50"
      : variant === "blue"
        ? "bg-white text-[#2E4A92] border border-[#2E4A92]/30 shadow-md"
        : "bg-white/95 text-[#070e1e] border border-[#070e1e]/15 shadow-sm";

  const sizing =
    size === "lg"
      ? "px-2 py-1 text-xs gap-1.5"
      : size === "md"
        ? "px-1.5 py-1 text-[11px] gap-1"
        : "px-1.5 py-0.5 text-[10px] gap-1";

  const iconSize = size === "lg" ? "h-4 w-4" : size === "md" ? "h-3.5 w-3.5" : "h-3 w-3";

  const label =
    count === 1
      ? t("video_pdf.has_one", "Includes 1 downloadable resource (PDF)")
      : t("video_pdf.has_many", "Includes {{n}} downloadable resources", { n: count });

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "absolute bottom-2 right-2 z-10 inline-flex items-center rounded-md font-semibold backdrop-blur-sm",
              palette,
              sizing,
              className,
            )}
            aria-label={label}
          >
            <FileText className={iconSize} />
            {count > 1 && <span>{count}</span>}
          </div>
        </TooltipTrigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={6}
            className="z-[100] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
          >
            {label}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );
};

export default VideoPdfIndicator;
